'use client';

import { useEffect, useState } from 'react';
import { LogOut, ExternalLink, Users } from 'lucide-react';
import {
  AmbientBackground,
  Button,
  Badge,
  type BadgeTone,
  IconTile,
  PageHeader,
  HeroSection,
  PageFooter,
  PageShell,
  PageMain,
  LoadingState,
  DemoIcon,
  LanguageToggle,
} from '../ui';
import type { Demo } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

const STATUS_LABEL: Record<string, string> = {
  running: 'Online',
  stopped: 'Offline',
  error: 'Erro',
  unknown: 'N/D',
  done: 'Online',
};

const STATUS_TONE: Record<string, BadgeTone> = {
  running: 'teal',
  done: 'teal',
  stopped: 'neutral',
  error: 'danger',
  unknown: 'neutral',
};



export const Hub = ({
  onLogout,
  token,
  isAdmin,
  onNavigateUsers,
}: {
  onLogout: () => void;
  token: string;
  isAdmin: boolean;
  onNavigateUsers: () => void;
}) => {
  const { t, translateDynamic } = useLanguage();
  const [demos, setDemos] = useState<Demo[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch('/api/demos', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res.status === 401) {
          onLogout();
          throw new Error(t('error.unauthorized'));
        }
        if (!res.ok) throw new Error(t('error.fetchProjects'));
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setDemos(data);
          setTimeout(() => setLoaded(true), 100);
        }
      })
      .catch(console.error);
  }, [token, onLogout]);

  const handleOpen = async (demo: Demo) => {
    if (demo.url) {
      window.open(demo.url, '_blank');
    }
  };

  return (
    <PageShell>
      <AmbientBackground scanline />

      <PageHeader
        navSlot={
          isAdmin && (
            <Button variant="ghost" onClick={onNavigateUsers} aria-label={t('hub.userManagementBtn')} className="px-3.5 py-1.5 text-[0.8rem]">
              <Users size={15} />
              <span>{t('hub.userManagementBtn')}</span>
            </Button>
          )
        }
        actionsSlot={
          <>
            <LanguageToggle />
            <div className="flex items-center gap-3.5">
              <span className="h-2 w-2 animate-pulse-dot rounded-full bg-brand-teal shadow-[0_0_8px_#46c0ba]" />
              <span className="text-sm text-white/45">
                {demos.length} {demos.length !== 1 ? t('hub.activeSystems') : t('hub.activeSystem')}
              </span>
            </div>
            <Button variant="ghost" onClick={onLogout} aria-label={t('hub.logoutBtn')} className="px-3.5 py-1.5 text-[0.8rem]">
              <LogOut size={16} />
              <span>{t('hub.logoutBtn')}</span>
            </Button>
          </>
        }
      />

      <PageMain>
        <HeroSection
          className="mb-14"
          badge={t('hub.heroBadge')}
          title={t('hub.heroTitle1')}
          gradientTitle={t('hub.heroTitle2')}
          description={t('hub.heroDesc')}
        />

        <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-6">
          {demos.length === 0 && <LoadingState label={t('hub.syncing')} />}
          {demos.map((demo, i) => (
            <div
              key={demo.id}
              className={`group relative flex flex-col overflow-hidden rounded-2xl border border-brand-teal/12 bg-white/3 p-6 transition-all duration-400 hover:-translate-y-1 hover:border-brand-teal/35 hover:shadow-[0_0_0_1px_rgba(70,192,186,0.08),0_20px_40px_rgba(0,0,0,0.4),0_0_60px_rgba(70,192,186,0.06)] ${
                loaded ? 'animate-card-in opacity-100' : 'translate-y-[30px] opacity-0'
              }`}
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-brand-teal to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              />

              <div className="mb-4 flex items-start justify-between">
                <IconTile>
                  <DemoIcon name={demo.icon} />
                </IconTile>
                {demo.status && (
                  <Badge tone={STATUS_TONE[demo.status] ?? 'neutral'} dot pulse={demo.status === 'running' || demo.status === 'done'}>
                    {t(`status.${demo.status}`) || (STATUS_LABEL[demo.status] ?? demo.status)}
                  </Badge>
                )}
              </div>

              <h3 className="m-0 mb-2 text-[1.1rem] font-bold tracking-[-0.01em] text-white">{demo.name}</h3>
              <p className="m-0 mb-5 grow text-[0.83rem] leading-[1.6] text-white/40">{translateDynamic(demo.description)}</p>

              <div className="mb-5 flex flex-wrap gap-4">
                <div className="flex items-center gap-1.5 text-xs text-white/30">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>{translateDynamic(demo.createdLabel || '—')}</span>
                </div>
                <div className="ml-auto flex items-center gap-1.5 text-xs text-brand-teal/70">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                  <span>
                    {demo.serviceCount ?? 0} {(demo.serviceCount ?? 0) !== 1 ? t('hub.services') : t('hub.service')}
                  </span>
                </div>
              </div>

              <Button
                className="group/btn relative w-full overflow-hidden"
                onClick={() => handleOpen(demo)}
                id={`hub-open-${demo.id}`}
              >
                <ExternalLink size={15} />
                {t('hub.accessBtn')}
                <span className="ml-auto text-[1.1rem] leading-none transition-transform duration-200 group-hover/btn:translate-x-1">›</span>
              </Button>
            </div>
          ))}
        </div>

        <PageFooter />
      </PageMain>
    </PageShell>
  );
};
